import { ListIcon, ListItem, Spacer } from '@chakra-ui/react'
import React, {
  ComponentProps,
  useCallback,
  useContext,
  useMemo,
  FC,
  ReactNode,
} from 'react'
import { ExternalLink } from '../../icons'
import { useColor } from '../../theme'
import { HeaderPopoverContext } from './HeaderPopover'
import { ICON_SIZE } from './constants'

interface IHeaderPopoverListItemProps {
  actionItem?: ReactNode
  dataTestId?: string
  icon?: ComponentProps<typeof ListIcon>['as']
  rightIcon?: ComponentProps<typeof ListIcon>['as']
  isActionItem?: boolean
  isExternal?: boolean
  isSecondary?: boolean
  isDisabled?: boolean
  id?: string
  content: ReactNode
  shouldCloseOnTimeOut?: boolean
  onClick?: () => void
}
/**
 * List item for the popover list in the header menu.
 *
 * Can be marked as `isExternal` and will display an additional icon on the
 * right.
 *
 * Can be flagged as `isSecondary` and will be entirely rendered in grey color.
 */
export const HeaderPopoverListItem: FC<IHeaderPopoverListItemProps> = ({
  icon,
  actionItem,
  isActionItem = false,
  isExternal = false,
  isSecondary = false,
  isDisabled = false,
  dataTestId,
  onClick,
  content,
  id,
  shouldCloseOnTimeOut = false,
  rightIcon,
}) => {
  const { forceClose } = useContext(HeaderPopoverContext)

  const itemColor = useColor({ nuance: 5, palette: 'primary' })
  const actionItemColor = useColor({ nuance: 5, palette: 'text' })
  const hoverColor = useColor({ nuance: 0, palette: 'secondary' })

  const currentItemColor = useMemo(
    () => (isActionItem ? actionItemColor : itemColor),
    [isActionItem, itemColor, actionItemColor],
  )

  const handleOnClick = useCallback(() => {
    if (onClick) {
      onClick()

      if (shouldCloseOnTimeOut) {
        setTimeout(() => {
          forceClose()
        }, 2000)

        return
      }
      forceClose()
    }
  }, [forceClose, onClick, shouldCloseOnTimeOut])

  return (
    <ListItem
      _hover={isDisabled ? undefined : {
        backgroundColor: hoverColor,
        borderRadius: 'lg',
      }}
      alignItems="center"
      as="button"
      color={currentItemColor}
      cursor={isDisabled ? 'default' : 'pointer'}
      data-testid={
        dataTestId ??
        `header-popover-${isSecondary ? 'secondary-' : ''}list-item`
      }
      disabled={isDisabled}
      display="flex"
      fontSize="md"
      fontWeight="600"
      h={10}
      id={id}
      onClick={handleOnClick}
      px={2}
      w="full">
      {icon && (
        <ListIcon
          as={icon}
          color={currentItemColor}
          fontSize={ICON_SIZE}
          opacity={isDisabled ? 0.3 : 1}
        />
      )}

      {content}

      {isExternal && (
        <>
          <Spacer />
          <ExternalLink
            color={currentItemColor}
            data-testid="header-popover-list-item-is-external"
            fontSize={ICON_SIZE}
          />
        </>
      )}

      {rightIcon && (
        <>
          <Spacer />
          <ListIcon
            as={rightIcon}
            color={currentItemColor}
            fontSize={ICON_SIZE}
            marginInlineEnd={0}
            opacity={isDisabled ? 0.3 : 1}
          />
        </>
      )}

      {actionItem}
    </ListItem>
  )
}
